import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import numeral from "numeral";
import {
  durationInMonthOptions,
  depositAndPurchaseFrequencyOptions,
} from "../../utilities/select_options";
import { Frame } from "@bitesite/react-figstrap";
import {
  Text,
  SegmentControl,
  Segment,
  BackArrow,
  Select,
  inEmbedMode,
  queryStringParams,
  Colors,
} from "../../odc-ui";
import TopUpTableModal from "../top_up_table_modal";
import styled from "styled-components/macro";
import useCurrentTopUpCampaign from "../../hooks/use_current_top_up_campaign";
import moment from "moment";

import HealthcoinLogo from "../../../assets/images/ODC_HealthPower_and_Coin.png";

import { LabelWithHintFrame, InlineHint } from "./common_ui_elements";

import PrintBanner from "./print_banner";
import ResultsPane from "./results_pane";

const HealthcoinLogoImg = styled.img`
  height: 40px;
`;

const PrintH1 = styled.h1`
  text-align: center;
  margin: 0;
  font-size: 20px;
`;

const PrintH2 = styled.h2`
  text-align: center;
  margin: 5px 0;
  font-size: 18px;
  font-weight: normal;
`;

const DateContainer = styled.div`
  text-align: center;
`;

const NoBreakText = styled(Text)`
  white-space: nowrap;
`;

const FixedValueContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;

function RewardCalculator({
  printMode,
  hideTreatmentCategorySwitcher,
  hideModeSwitcher,
  hideAutoGrowSwitcher,
  defaultTreatmentCategory,
  defaultInitialBalance,
  defaultGoalBalance,
  defaultBalanceDurationInMonths,
  defaultCalculatorMode,
  defaultAutoGrowMode,
  defaultOngoingDepositsAmount,
  defaultOngoingDepositsFrequency,
  defaultOngoingClinicPurchasesAmount,
  defaultOngoingClinicPurchasesFrequency,
  defaultRewardRate,
  defaultTopUpAmountForInitialBalance,
  defaultTopUpAmountForIndividualOngoingDeposit,
  defaultTimeToHitGoalInMonths,
  backUrl,
}) {
  /* Constants and reusable values ============================================================ */
  const DERMATOLOGY_MODE_STRING = "dermatology";
  const SURGERY_MODE_STRING = "surgery";
  const REWARDS_MODE_STRING = "rewards";
  const GOAL_MODE_STRING = "goal";
  const HEALTHCARE_GOAL_AUTO_GROW_MODE_STRING = "healthcare-goal-auto-grow";
  const MONTHLY_AUTO_GROW_MODE_STRING = "monthly-auto-grow";

  const initialFrequencyOption = depositAndPurchaseFrequencyOptions.find(
    (option) => {
      return option.value === "weekly";
    }
  );

  const monthlyFrequencyOption = depositAndPurchaseFrequencyOptions.find(
    (option) => {
      return option.value === "monthly";
    }
  );

  const defaultMonthlyAutoGrowModeInitialBalanceOption = {
    label: "$0",
    value: 0,
  };

  const defaultMonthlyAutoGrowModeOngoingDepositsAmountOption = {
    label: "$199",
    value: 199,
  };

  const surgeryAutoGrowInitialBalanceOptions = [
    {
      label: "$0",
      value: 0,
    },
    {
      label: "$250",
      value: 250,
    },
    {
      label: "$500",
      value: 500,
    },
    {
      label: "$1,000",
      value: 1000,
    },
    {
      label: "$2,000",
      value: 2000,
    },
    {
      label: "$3,000",
      value: 3000,
    },
    {
      label: "$4,000",
      value: 4000,
    },
    {
      label: "$5,000",
      value: 5000,
    },
    {
      label: "$6,000",
      value: 6000,
    },
    {
      label: "$7,000",
      value: 7000,
    },
    {
      label: "$8,000",
      value: 8000,
    },
    {
      label: "$9,000",
      value: 9000,
    },
    {
      label: "$10,000",
      value: 10000,
    },
  ];

  /* Default Setup for Print to PDF ============================================================ */
  let defaultInitialBalanceOption = null;
  if (defaultInitialBalance) {
    defaultInitialBalanceOption = {
      label: numeral(defaultInitialBalance).format("$0,0"),
      value: defaultInitialBalance,
    };
  }

  let defaultGoalBalanceOption = null;
  if (defaultGoalBalance) {
    defaultGoalBalanceOption = {
      label: numeral(defaultGoalBalance).format("$0,0"),
      value: defaultGoalBalance,
    };
  }

  let defaultBalanceDurationInMonthsOption = null;
  if (defaultBalanceDurationInMonths) {
    const unitString =
      defaultBalanceDurationInMonths === 1 ? "Month" : "Months";

    defaultBalanceDurationInMonthsOption = {
      label: `${defaultBalanceDurationInMonths} ${unitString}`,
      value: defaultBalanceDurationInMonths,
    };
  }

  let defaultOngoingDepositsAmountOption = null;
  if (defaultOngoingDepositsAmount) {
    defaultOngoingDepositsAmountOption = {
      label: numeral(defaultOngoingDepositsAmount).format("$0,0"),
      value: defaultOngoingDepositsAmount,
    };
  }

  let defaultOngoingDepositsFrequencyOption = initialFrequencyOption;
  if (defaultOngoingDepositsFrequency) {
    defaultOngoingDepositsFrequencyOption =
      depositAndPurchaseFrequencyOptions.find((option) => {
        return option.value === defaultOngoingDepositsFrequency;
      });
  }

  let defaultOngoingClinicPurchasesFrequencyOption = initialFrequencyOption;
  if (defaultOngoingClinicPurchasesFrequency) {
    defaultOngoingClinicPurchasesFrequencyOption =
      depositAndPurchaseFrequencyOptions.find((option) => {
        return option.value === defaultOngoingClinicPurchasesFrequency;
      });
  }

  /* STATE VARIABLES SETUP ============================================================ */
  const [treatmentCategory, setTreatmentCategory] = useState(
    defaultTreatmentCategory || DERMATOLOGY_MODE_STRING
  );

  const [calculatorMode, setCalculatorMode] = useState(
    defaultCalculatorMode || REWARDS_MODE_STRING
  );

  const [autoGrowMode, setAutoGrowMode] = useState(
    defaultAutoGrowMode || MONTHLY_AUTO_GROW_MODE_STRING
  );

  const [goalBalanceOption, setGoalBalanceOption] = useState(
    defaultGoalBalanceOption
  );
  const goalBalance = goalBalanceOption && goalBalanceOption.value;

  const [initialBalanceOption, setInitialBalanceOption] = useState(
    defaultInitialBalanceOption
  );
  const initialBalance = initialBalanceOption && initialBalanceOption.value;

  const [balanceDurationInMonthsOption, setBalanceDurationInMonthsOption] =
    useState(defaultBalanceDurationInMonthsOption);

  const [ongoingClinicPurchasesAmount, setOngoingClinicPurchasesAmount] =
    useState(defaultOngoingClinicPurchasesAmount || "");

  const [topUpAmountForInitialBalance, setTopUpAmountForInitialBalance] =
    useState(defaultTopUpAmountForInitialBalance || 0);
  const [
    topUpAmountForIndividualOngoingDeposit,
    setTopUpAmountForIndividualOngoingDeposit,
  ] = useState(defaultTopUpAmountForIndividualOngoingDeposit || 0);

  const [
    ongoingClinicPurchasesFrequencyOption,
    setOngoingClinicPurchasesFrequencyOption,
  ] = useState(defaultOngoingClinicPurchasesFrequencyOption);

  const [ongoingDepositsAmountOption, setOngoingDepositsAmountOption] =
    useState(defaultOngoingDepositsAmountOption);
  const ongoingDepositsAmount =
    ongoingDepositsAmountOption && ongoingDepositsAmountOption.value;

  const [ongoingDepositsFrequencyOption, setOngoingDepositsFrequencyOption] =
    useState(defaultOngoingDepositsFrequencyOption);

  const [rewardRate, setRewardRate] = useState(defaultRewardRate || "");
  const [products, setProducts] = useState([]);
  const [showTopUpTable, setShowTopUpTable] = useState(false);
  const [admin, setAdmin] = useState(false);

  const [timeToHitGoalInMonths, setTimeToHitGoalInMonths] = useState(
    defaultTimeToHitGoalInMonths || 0
  );

  const [readyToPrint, setReadyToPrint] = useState(false);

  const COMPOUND_PERIODS_PER_YEAR = 365.0;

  /* Top up Setup */
  /* Note, today purchaseMode happens to line up with Goal vs Reward mode. This may not always be the case */
  const purchaseMode = inGoalMode() ? "auto_grow" : "prepaid";

  let { currentTopUpCampaign } = useCurrentTopUpCampaign(
    true,
    treatmentCategory,
    purchaseMode
  );

  /* Calculator Modes =================================================================  */
  function inDermatologyMode() {
    return treatmentCategory === DERMATOLOGY_MODE_STRING;
  }

  function inSurgeryMode() {
    return treatmentCategory === SURGERY_MODE_STRING;
  }

  function inRewardsMode() {
    return calculatorMode === REWARDS_MODE_STRING;
  }

  function inGoalMode() {
    return calculatorMode === GOAL_MODE_STRING;
  }

  function inHealthcareGoalAutoGrowMode() {
    return autoGrowMode === HEALTHCARE_GOAL_AUTO_GROW_MODE_STRING;
  }

  function inMonthlyAutoGrowMode() {
    return autoGrowMode === MONTHLY_AUTO_GROW_MODE_STRING;
  }

  /* Calculator Functions ============================================================== */
  function investmentDurationInMonths() {
    let durationInMonths = 0;
    if (inRewardsMode() || (inGoalMode() && inMonthlyAutoGrowMode())) {
      durationInMonths = balanceDurationInMonthsOption
        ? balanceDurationInMonthsOption.value
        : 0;
    }

    if (inGoalMode() && inHealthcareGoalAutoGrowMode()) {
      durationInMonths = timeToHitGoalInMonths;
    }

    return durationInMonths;
  }

  function futureValueOfAnnuity(r, cy, pmt, py, t) {
    /* https://math.libretexts.org/Bookshelves/Applied_Mathematics/Business_Math_(Olivier)/11%3A_Compound_Interest_Annuities/11.02%3A_Future_Value_Of_Annuities */
    const i = r / cy;
    const n = t * py;

    /* deposits made at end of period */
    const amount =
      pmt * ((((1 + i) ** (cy / py)) ** n - 1) / ((1 + i) ** (cy / py) - 1));

    /* if deposits make at beginning of period */
    /* TODO */

    return amount;
  }

  function totalNumberOfRegularTransactions(py) {
    const balanceDurationInYears =
      parseFloat(investmentDurationInMonths()) / 12.0;
    return Math.floor(balanceDurationInYears * py);
  }

  function totalAmountFromRegularTransactions(amount, py) {
    return amount * totalNumberOfRegularTransactions(py);
  }

  function totalClinicPurchases() {
    const { py } =
      ongoingClinicPurchasesFrequencyOption ||
      defaultOngoingClinicPurchasesFrequencyOption;
    return totalAmountFromRegularTransactions(
      parsedFloatOrZero(ongoingClinicPurchasesAmount),
      py
    );
  }

  function totalAdditionalDeposits() {
    const { py } =
      ongoingDepositsFrequencyOption || defaultOngoingDepositsFrequencyOption;
    return totalAmountFromRegularTransactions(
      parsedFloatOrZero(ongoingDepositsAmount),
      py
    );
  }

  function totalInitialAmountIncludingTopUp() {
    return (
      parsedFloatOrZero(initialBalance) +
      parsedFloatOrZero(topUpAmountForInitialBalance)
    );
  }

  function totalOutOfPocketContributions() {
    return (
      parsedFloatOrZero(initialBalance) +
      parsedFloatOrZero(totalAdditionalDeposits())
    );
  }

  function ongoingDepositAmountIncludingTopUp() {
    return (
      parsedFloatOrZero(ongoingDepositsAmount) +
      parsedFloatOrZero(topUpAmountForIndividualOngoingDeposit)
    );
  }

  function parsedFloatOrZero(value) {
    return Number.isNaN(parseFloat(value)) ? 0 : parseFloat(value);
  }

  function rewardRateDecimal() {
    return parseFloat(rewardRate) / 100.0;
  }

  function futureValueOfInitialBalance(durationInMonths) {
    const n = COMPOUND_PERIODS_PER_YEAR;
    const r = rewardRateDecimal();
    const t = parsedFloatOrZero(durationInMonths) / 12.0;
    const topUp = totalInitialAmountIncludingTopUp();
    const amount = topUp * (1.0 + r / n) ** (n * t);

    return amount;
  }

  function futureValueOfOngoingDeposits(durationInMonths) {
    const cy = COMPOUND_PERIODS_PER_YEAR;
    const r = parseFloat(rewardRateDecimal());
    const pmt = parsedFloatOrZero(ongoingDepositAmountIncludingTopUp());
    const { py } = ongoingDepositsFrequencyOption || initialFrequencyOption;
    let t = parsedFloatOrZero(durationInMonths) / 12.0;

    if (ongoingDepositsFrequencyOption.value === "yearly") {
      t = Math.floor(t);
    }

    return futureValueOfAnnuity(r, cy, pmt, py, t);
  }

  function futureValueOfOngoingClinicPurchases(durationInMonths) {
    const cy = COMPOUND_PERIODS_PER_YEAR;
    const r = parseFloat(rewardRateDecimal());
    const pmt = parsedFloatOrZero(ongoingClinicPurchasesAmount);
    const { py, value } =
      ongoingClinicPurchasesFrequencyOption || initialFrequencyOption;
    let t = parsedFloatOrZero(durationInMonths) / 12.0;

    if (value === "yearly") {
      t = Math.floor(t);
    }
    return futureValueOfAnnuity(r, cy, pmt, py, t);
  }

  function futureHealthCoinBalance(durationInMonths) {
    if (durationInMonths === undefined) {
      durationInMonths = balanceDurationInMonthsOption
        ? balanceDurationInMonthsOption.value
        : 0;
    }

    const value =
      futureValueOfInitialBalance(durationInMonths) +
      futureValueOfOngoingDeposits(durationInMonths) -
      futureValueOfOngoingClinicPurchases(durationInMonths);

    return value;
  }

  function calculateTimeToHitGoalInMonths() {
    let calculatedBalance = 0;
    let numberOfMonths = 1;

    calculatedBalance = futureHealthCoinBalance(numberOfMonths);

    /* Fail safe at 5000 months */
    while (numberOfMonths < 5000) {
      calculatedBalance = futureHealthCoinBalance(numberOfMonths);
      if (calculatedBalance >= goalBalance) {
        setTimeToHitGoalInMonths(numberOfMonths);
        break;
      }

      numberOfMonths++;
    }
  }

  function totalHealthCoinRewardsEarned() {
    return (
      futureHealthCoinBalance(investmentDurationInMonths()) -
      initialBalance -
      (totalAdditionalDeposits() - totalClinicPurchases())
    );
  }

  function totalHealthCoinRewardsEarnedExcludingTopUps() {
    return totalHealthCoinRewardsEarned() - topUpTotal();
  }

  function setValidDepositAndPurchaseFrequencyOptions() {
    if (inGoalMode() && inMonthlyAutoGrowMode()) {
      depositAndPurchaseFrequencyOptions[0].disabled = true;
      depositAndPurchaseFrequencyOptions[1].disabled = true;
      depositAndPurchaseFrequencyOptions[2].disabled = false;
      depositAndPurchaseFrequencyOptions[3].disabled = true;
      depositAndPurchaseFrequencyOptions[4].disabled = true;
    } else {
      if (
        !balanceDurationInMonthsOption ||
        balanceDurationInMonthsOption.value < 3
      ) {
        depositAndPurchaseFrequencyOptions[0].disabled = false;
        depositAndPurchaseFrequencyOptions[1].disabled = false;
        depositAndPurchaseFrequencyOptions[2].disabled = false;
        depositAndPurchaseFrequencyOptions[3].disabled = true;
        depositAndPurchaseFrequencyOptions[4].disabled = true;
      } else if (
        balanceDurationInMonthsOption.value >= 3 &&
        balanceDurationInMonthsOption.value < 6
      ) {
        depositAndPurchaseFrequencyOptions[0].disabled = false;
        depositAndPurchaseFrequencyOptions[1].disabled = false;
        depositAndPurchaseFrequencyOptions[2].disabled = false;
        depositAndPurchaseFrequencyOptions[3].disabled = false;
        depositAndPurchaseFrequencyOptions[4].disabled = true;
      } else if (balanceDurationInMonthsOption.value >= 6) {
        depositAndPurchaseFrequencyOptions[0].disabled = false;
        depositAndPurchaseFrequencyOptions[1].disabled = false;
        depositAndPurchaseFrequencyOptions[2].disabled = false;
        depositAndPurchaseFrequencyOptions[3].disabled = false;
        depositAndPurchaseFrequencyOptions[4].disabled = false;
      }
    }

    checkIfOngoingClinicPurchasesFrequencyIsStillValid();
  }

  function checkIfOngoingClinicPurchasesFrequencyIsStillValid() {
    if (
      ongoingClinicPurchasesFrequencyOption &&
      ongoingClinicPurchasesFrequencyOption.disabled
    ) {
      setOngoingClinicPurchasesFrequencyOption(
        depositAndPurchaseFrequencyOptions[0]
      );
    }
  }

  function loadRewardRate() {
    $.getJSON("/settings/reward_rate")
      .done((results) => {
        setRewardRate(results.reward_rate);
      })
      .fail(() => {
        window.alert("We're sorry, but we couldn't load the reward rate.");
      });
  }

  function loadProducts() {
    let data = { treatment_category: treatmentCategory };

    $.getJSON("/products", data)
      .done((results) => {
        setProducts(results);
      })
      .fail(() => {
        window.alert(
          "We're sorry, but we couldn't load the fund denominations."
        );
      });
  }

  function goalOptions() {
    let i = 1000;
    const options = [];
    while (i <= 30000) {
      options.push({
        label: numeral(i).format("$0,0"),
        value: i,
      });
      i += 1000;
    }

    return options;
  }

  function selectOptionFromProduct(product) {
    return {
      label: numeral(product.price).format("$0,0"),
      value: product.price,
    };
  }

  function defaultInvestmentOptions() {
    let options = [];
    if (inDermatologyMode()) {
      options = products
        .filter((product) => product.enabled_for_dermatology_prepaid)
        .map((product) => selectOptionFromProduct(product));
    }

    if (inSurgeryMode()) {
      options = products
        .filter((product) => product.enabled_for_surgery_prepaid)
        .map((product) => selectOptionFromProduct(product));
    }

    options.unshift({ label: "$0", value: 0 });
    return options;
  }

  function initialBalanceOptions() {
    if (inGoalMode() && inMonthlyAutoGrowMode()) {
      return [defaultMonthlyAutoGrowModeInitialBalanceOption];
    }

    if (inSurgeryMode() && inGoalMode() && inHealthcareGoalAutoGrowMode()) {
      return surgeryAutoGrowInitialBalanceOptions;
    }

    return defaultInvestmentOptions();
  }

  function ongoingDepositsAmountOptions() {
    if (inDermatologyMode()) {
      const options = products
        .filter((product) => product.enabled_for_dermatology_auto_grow)
        .map((product) => selectOptionFromProduct(product));

      return options;
    }

    if (inSurgeryMode()) {
      const options = products
        .filter((product) => product.enabled_for_surgery_auto_grow)
        .map((product) => selectOptionFromProduct(product));

      return options;
    }

    return [];
  }

  function handleTopUpTableToggle(e) {
    e.preventDefault();
    setShowTopUpTable((prevState) => !prevState);
  }

  function campaignInProgress() {
    return currentTopUpCampaign && currentTopUpCampaign.id;
  }

  function topUpTotal() {
    const value =
      topUpAmountForInitialBalance +
      topUpAmountForIndividualOngoingDeposit *
        totalNumberOfRegularTransactions(ongoingDepositsFrequencyOption.py);
    return value;
  }

  /* Graph Functions =============================================================================================== */
  function generateGraphData() {
    const data = [];

    if (
      inGoalMode() &&
      ((inMonthlyAutoGrowMode() && balanceDurationInMonthsOption) ||
        (inHealthcareGoalAutoGrowMode() && timeToHitGoalInMonths))
    ) {
      const maxMonths = inMonthlyAutoGrowMode()
        ? balanceDurationInMonthsOption.value
        : timeToHitGoalInMonths;

      for (let monthNumber = 1; monthNumber <= maxMonths; monthNumber += 1) {
        data.push({
          month: monthNumber,
          balance: numeral(futureHealthCoinBalance(monthNumber)).format("0.00"),
        });
      }
    }

    return data;
  }

  /* Partial Renders =============================================================================================== */
  function renderInvestmentDurationSection() {
    let filteredDurationInMonthOptions = durationInMonthOptions;

    if (inGoalMode() && inMonthlyAutoGrowMode()) {
      filteredDurationInMonthOptions = filteredDurationInMonthOptions.filter(
        (monthOption) => monthOption.value >= 3
      );
    }

    return (
      <div className="frame-109 fgs-ali fgs-al fgs-al-h fgs-al-g-30 fgs-al-grow">
        <div
          className={`fgs-ali fgs-al ${
            printMode
              ? "fgs-al-h fgs-al-align-items-center fgs-al-justify-content-space-between"
              : "fgs-al-v"
          } fgs-al-g-10`}
        >
          <LabelWithHintFrame className="label small-medium">
            <Text color={textColor}>My Investment Duration</Text>
            {!printMode && (
              <InlineHint marginLeft={10}>
                Amount of time you expect your healthcare savings journey to
                last.
              </InlineHint>
            )}
          </LabelWithHintFrame>

          {printMode ? (
            <Text className="label small-medium">
              {balanceDurationInMonthsOption &&
                balanceDurationInMonthsOption.label}
            </Text>
          ) : (
            <Select
              options={filteredDurationInMonthOptions}
              placeholder="eg: 12 Months"
              value={balanceDurationInMonthsOption}
              onChange={(option) => {
                setBalanceDurationInMonthsOption(option);
              }}
              isSearchable={false}
            />
          )}
        </div>
      </div>
    );
  }

  /* Top up calculations =============================================================================================== */
  function calculateTopUpForInitialBalance() {
    if (
      initialBalance &&
      currentTopUpCampaign &&
      currentTopUpCampaign.top_up_campaign_items
    ) {
      const topUpCampaignItemForInitialBalance =
        currentTopUpCampaign.top_up_campaign_items.find((topUpCampaignItem) => {
          return topUpCampaignItem.product.price == initialBalance;
        });

      if (topUpCampaignItemForInitialBalance) {
        setTopUpAmountForInitialBalance(
          topUpCampaignItemForInitialBalance.amount_in_hundredths / 100
        );
      } else {
        setTopUpAmountForInitialBalance(0);
      }
    } else {
      setTopUpAmountForInitialBalance(0);
    }
  }

  function calculateTopUpForIndividualOngoingDeposit() {
    if (
      ongoingDepositsAmount &&
      currentTopUpCampaign &&
      currentTopUpCampaign.top_up_campaign_items &&
      !printMode
    ) {
      console.log({ currentTopUpCampaign, ongoingDepositsAmount });

      /* Matching by value, and only considering non-zero top-ups. */
      const topUpCampaignItemForOngoingDepositsAmount =
        currentTopUpCampaign.top_up_campaign_items.find((topUpCampaignItem) => {
          if (inSurgeryMode()) {
            return (
              topUpCampaignItem.product.price == ongoingDepositsAmount &&
              topUpCampaignItem.amount_in_hundredths != 0 &&
              topUpCampaignItem.product.enabled_for_surgery_auto_grow
            );
          } else if (inDermatologyMode()) {
            return (
              topUpCampaignItem.product.price == ongoingDepositsAmount &&
              topUpCampaignItem.amount_in_hundredths != 0 &&
              topUpCampaignItem.product.enabled_for_dermatology_auto_grow
            );
          } else {
            return false;
          }
        });

      console.log({ topUpCampaignItemForOngoingDepositsAmount });
      if (topUpCampaignItemForOngoingDepositsAmount) {
        console.log("Top up being set");
        setTopUpAmountForIndividualOngoingDeposit(
          topUpCampaignItemForOngoingDepositsAmount.amount_in_hundredths / 100.0
        );
      } else {
        console.log("Top up not found, setting to 0.");
        setTopUpAmountForIndividualOngoingDeposit(0);
      }
    } else {
      setTopUpAmountForIndividualOngoingDeposit(0);
    }
  }

  /* useEffects =============================================================================================== */
  useEffect(() => {
    if (window.gon && window.gon.current_user_role_names.includes("admin")) {
      setAdmin(true);
    }
    setReadyToPrint(true);
  }, []);

  useEffect(() => {
    if (!printMode) {
      loadRewardRate();
      loadProducts();
      setValidDepositAndPurchaseFrequencyOptions();
    }
  }, [balanceDurationInMonthsOption]);

  useEffect(() => {
    if (!printMode) {
      loadProducts();
    }
  }, [treatmentCategory]);

  useEffect(() => {
    if (!printMode) {
      calculateTopUpForInitialBalance();
    }
  }, [initialBalance, currentTopUpCampaign]);

  useEffect(() => {
    if (!printMode) {
      calculateTopUpForIndividualOngoingDeposit();
    }
  }, [ongoingDepositsAmount, currentTopUpCampaign]);

  useEffect(() => {
    if (inGoalMode() && !printMode) {
      setOngoingClinicPurchasesAmount("");
    }
    if (inRewardsMode() && !printMode) {
      setOngoingDepositsAmountOption({ value: 0 });
    }
  }, [calculatorMode]);

  useEffect(() => {
    if (!printMode) {
      if (inGoalMode()) {
        if (inMonthlyAutoGrowMode()) {
          // Monthly Auto Grow Mode Reset
          setInitialBalanceOption(
            defaultMonthlyAutoGrowModeInitialBalanceOption
          );
          setOngoingDepositsAmountOption(
            defaultMonthlyAutoGrowModeOngoingDepositsAmountOption
          );
          setOngoingDepositsFrequencyOption(monthlyFrequencyOption);
        } else {
          // Healthcare Goal Auto Grow Mode Reset
          setInitialBalanceOption(defaultInitialBalanceOption);
          setOngoingDepositsAmountOption(defaultOngoingDepositsAmountOption);
          setOngoingDepositsFrequencyOption(
            defaultOngoingDepositsFrequencyOption
          );
        }
      } else {
        // Reward Mode Reset
        setInitialBalanceOption(defaultInitialBalanceOption);
        setOngoingDepositsAmountOption(defaultOngoingDepositsAmountOption);
        setOngoingDepositsFrequencyOption(
          defaultOngoingDepositsFrequencyOption
        );
      }

      setValidDepositAndPurchaseFrequencyOptions();
    }
  }, [calculatorMode, autoGrowMode]);

  useEffect(() => {
    if (!printMode) {
      calculateTimeToHitGoalInMonths();
    }
  }, [
    initialBalance,
    goalBalance,
    ongoingDepositsAmount,
    ongoingDepositsFrequencyOption,
    rewardRate,
    currentTopUpCampaign,
    topUpAmountForIndividualOngoingDeposit,
    topUpAmountForInitialBalance,
  ]);

  const textColor = inEmbedMode()
    ? queryStringParams().embed_text_color
    : Colors.darkGrey100;

  /* Render =============================================================================================== */
  return (
    <>
      {printMode && <PrintBanner readyToPrint={readyToPrint} />}
      <div
        className={`component-reward-calculator ${
          printMode ? "print-mode" : ""
        }`}
      >
        {printMode && (
          <Frame justifyContent="center">
            <HealthcoinLogoImg src={HealthcoinLogo} />
          </Frame>
        )}
        <Frame
          className="reward-container frame32"
          vertical
          padding={10}
          gap={40}
          alignItems="center"
        >
          {backUrl && (
            <div className="header-with-back-container">
              <div className="header-with-back">
                <a href="/">
                  <BackArrow />
                </a>
                <h1>Reward Calculator</h1>
              </div>
            </div>
          )}

          <Frame vertical gap={5} alignItems="center">
            {!printMode && !hideTreatmentCategorySwitcher && (
              <Frame alignItems="center" padding={10}>
                {!printMode && (
                  <SegmentControl>
                    <Segment
                      selected={inDermatologyMode()}
                      onClick={() =>
                        setTreatmentCategory(DERMATOLOGY_MODE_STRING)
                      }
                    >
                      Dermatology
                    </Segment>
                    <Segment
                      selected={inSurgeryMode()}
                      onClick={() => setTreatmentCategory(SURGERY_MODE_STRING)}
                    >
                      Surgery
                    </Segment>
                  </SegmentControl>
                )}
              </Frame>
            )}

            {!printMode && !hideModeSwitcher && (
              <Frame alignItems="center" padding={10}>
                {!printMode && (
                  <SegmentControl>
                    <Segment
                      selected={inRewardsMode()}
                      onClick={() => setCalculatorMode(REWARDS_MODE_STRING)}
                    >
                      Prepaid Rewards Calculator
                    </Segment>
                    <Segment
                      selected={inGoalMode()}
                      onClick={() => setCalculatorMode(GOAL_MODE_STRING)}
                    >
                      Auto Grow Rewards Calculator
                    </Segment>
                  </SegmentControl>
                )}
              </Frame>
            )}

            {!printMode && inGoalMode() && !hideAutoGrowSwitcher && (
              <Frame alignItems="center" padding={10}>
                <SegmentControl>
                  <Segment
                    selected={inMonthlyAutoGrowMode()}
                    onClick={() =>
                      setAutoGrowMode(MONTHLY_AUTO_GROW_MODE_STRING)
                    }
                  >
                    Monthly Auto Grow
                  </Segment>
                  <Segment
                    selected={inHealthcareGoalAutoGrowMode()}
                    onClick={() =>
                      setAutoGrowMode(HEALTHCARE_GOAL_AUTO_GROW_MODE_STRING)
                    }
                  >
                    Healthcare Goal Calculator
                  </Segment>
                </SegmentControl>
              </Frame>
            )}
          </Frame>

          {printMode && (
            <Frame vertical justifyContent="center">
              <PrintH1>
                Healthcoin<sup>®</sup> Rewards Calculator Results
              </PrintH1>
              <PrintH2>
                {inRewardsMode() && "My Rewards Projection"}
                {inGoalMode() &&
                  inHealthcareGoalAutoGrowMode() &&
                  "My Healthcare Goal"}
                {inGoalMode() &&
                  inMonthlyAutoGrowMode() &&
                  "My Monthly Auto Grow Projection"}
              </PrintH2>
              <DateContainer>
                <Text type="extra-small-medium">
                  {moment().format("dddd, MMMM Do YYYY, h:mm:ss a")}
                </Text>
              </DateContainer>
            </Frame>
          )}
          <div
            className={`fgs-ali frame-106 fgs-al ${
              printMode
                ? "fgs-al-v fgs-al-g-10 fgs-al-align-items-center"
                : "fgs-al-h fgs-al-g-30"
            }  fgs-al-m-v`}
          >
            {/* CALCULATOR PANE ================================================================= */}
            <Frame
              className={`input-area standard-form ${
                printMode ? "input-area-print-mode" : ""
              }`}
              vertical
              gap={printMode ? 20 : 30}
              padding={printMode ? 0 : 10}
            >
              {inGoalMode() && inHealthcareGoalAutoGrowMode() && (
                <Frame
                  vertical={!printMode}
                  gap={10}
                  alignItems={printMode ? "center" : undefined}
                  justifyContent={printMode ? "space-between" : undefined}
                >
                  <LabelWithHintFrame className="label small-medium">
                    <Text color={textColor}>My Healthcare Goal</Text>
                    {!printMode && (
                      <InlineHint marginLeft={10} popUpYOffset={30}>
                        The total cost of your healthcare experience (i.e., cost
                        of surgery/other procedure)
                      </InlineHint>
                    )}
                  </LabelWithHintFrame>

                  {printMode ? (
                    <Text className="label small-medium">
                      {goalBalanceOption && goalBalanceOption.label}
                    </Text>
                  ) : (
                    <Select
                      options={goalOptions()}
                      placeholder="eg: $1,000"
                      value={goalBalanceOption}
                      onChange={(option) => setGoalBalanceOption(option)}
                      isSearchable={false}
                    />
                  )}
                </Frame>
              )}

              {(inRewardsMode() ||
                (inGoalMode() && inHealthcareGoalAutoGrowMode())) && (
                <Frame
                  vertical={!printMode}
                  gap={10}
                  alignItems={printMode ? "center" : undefined}
                  justifyContent={printMode ? "space-between" : undefined}
                >
                  <LabelWithHintFrame className="label small-medium">
                    <Text color={textColor}>
                      {inRewardsMode()
                        ? "My Initial Healthcare Investment"
                        : "My Initial Investment"}
                    </Text>
                    {!printMode && (
                      <InlineHint marginLeft={10} popUpYOffset={20}>
                        {inRewardsMode()
                          ? "Starting investment amount, or total expected spending during your Healthcare Journey with us."
                          : "The amount you can contribute today"}
                      </InlineHint>
                    )}
                  </LabelWithHintFrame>

                  {printMode ? (
                    <Text className="label small-medium">
                      {initialBalanceOption && initialBalanceOption.label}
                    </Text>
                  ) : (
                    <Select
                      options={initialBalanceOptions()}
                      placeholder="eg: $1,000"
                      value={initialBalanceOption}
                      onChange={(option) => setInitialBalanceOption(option)}
                      isSearchable={false}
                    />
                  )}
                </Frame>
              )}

              {inRewardsMode() && renderInvestmentDurationSection()}

              {inGoalMode() && (
                <div
                  className={`fgs-ali fgs-al ${
                    printMode
                      ? "fgs-al-h fgs-al-align-items-center fgs-al-justify-content-space-between"
                      : "fgs-al-v"
                  } fgs-al-g-10`}
                >
                  <LabelWithHintFrame className="label small-medium">
                    <Text color={textColor}>My Regular Savings</Text>
                    {!printMode && (
                      <InlineHint marginLeft={10}>
                        Your expected recurring contribution
                      </InlineHint>
                    )}
                  </LabelWithHintFrame>
                  <div
                    className={`frame-109 fgs-al fgs-al-h ${
                      printMode ? "fgs-al-g-6" : "fgs-al-g-10"
                    } fgs-al-even`}
                  >
                    <div className="fgs-ali fgs-al fgs-al-v fgs-al-g-10 currency-wrap">
                      {printMode ? (
                        <Text className="small-medium label">
                          {ongoingDepositsAmountOption &&
                            ongoingDepositsAmountOption.label}
                        </Text>
                      ) : (
                        <Select
                          options={ongoingDepositsAmountOptions()}
                          placeholder="eg: $1000"
                          value={ongoingDepositsAmountOption}
                          onChange={(option) =>
                            setOngoingDepositsAmountOption(option)
                          }
                          isSearchable={false}
                        />
                      )}
                    </div>
                    <div className="fgs-ali fgs-al fgs-al-v fgs-al-g-10">
                      {printMode ? (
                        <NoBreakText className="small-medium label">
                          {ongoingDepositsFrequencyOption &&
                            ongoingDepositsFrequencyOption.label}
                        </NoBreakText>
                      ) : (
                        <>
                          {inGoalMode() && inMonthlyAutoGrowMode() ? (
                            <FixedValueContainer>
                              <Text color={textColor}>
                                {ongoingDepositsFrequencyOption &&
                                  ongoingDepositsFrequencyOption.label}
                              </Text>
                            </FixedValueContainer>
                          ) : (
                            <Select
                              options={depositAndPurchaseFrequencyOptions}
                              defaultValue={
                                depositAndPurchaseFrequencyOptions[0]
                              }
                              value={
                                ongoingDepositsFrequencyOption
                                  ? ongoingDepositsFrequencyOption
                                  : depositAndPurchaseFrequencyOptions[0]
                              }
                              isOptionDisabled={(option) => option.disabled}
                              onChange={(option) =>
                                setOngoingDepositsFrequencyOption(option)
                              }
                              isSearchable={false}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {inGoalMode() &&
                inMonthlyAutoGrowMode() &&
                renderInvestmentDurationSection()}

              {/* Reward Rate Display ================================================== */}
              {!printMode && (
                <Frame
                  vertical
                  gap={10}
                  padding={20}
                  className="reward-rate-display"
                >
                  <Text>Investment reward rate for patients:</Text>
                  <Frame horizontal gap={10} alignItems="center">
                    <Text type="large-semibold"> {rewardRate}% </Text>
                    <Frame horizontal>
                      <Text type="small-light" color="#707070">
                        annual rate, calculated daily, paid monthly
                      </Text>
                    </Frame>
                  </Frame>
                </Frame>
              )}

              {printMode && (
                <Frame
                  vertical
                  gap={5}
                  padding={10}
                  className="reward-rate-display reward-rate-display-print-mode"
                >
                  <div>
                    <Text type="regular-medium">
                      Investment reward rate for patients:
                    </Text>{" "}
                    <Text type="regular-bold">{rewardRate}%</Text>
                  </div>

                  <Text type="small-light" color="#707070">
                    annual rate, calculated daily, paid monthly
                  </Text>
                </Frame>
              )}
            </Frame>

            {/* RESULTS PANE ================================================================= */}
            <ResultsPane
              calculatorMode={calculatorMode}
              autoGrowMode={autoGrowMode}
              printMode={printMode}
              inDermatologyMode={inDermatologyMode}
              inSurgeryMode={inSurgeryMode}
              inGoalMode={inGoalMode}
              inRewardsMode={inRewardsMode}
              inHealthcareGoalAutoGrowMode={inHealthcareGoalAutoGrowMode}
              inMonthlyAutoGrowMode={inMonthlyAutoGrowMode}
              campaignInProgress={campaignInProgress}
              goalBalance={goalBalance}
              treatmentCategory={treatmentCategory}
              initialBalance={initialBalance}
              ongoingDepositsAmount={ongoingDepositsAmount}
              ongoingDepositsFrequencyOption={ongoingDepositsFrequencyOption}
              timeToHitGoalInMonths={timeToHitGoalInMonths}
              topUpTotal={topUpTotal}
              futureHealthCoinBalance={futureHealthCoinBalance}
              balanceDurationInMonthsOption={balanceDurationInMonthsOption}
              totalOutOfPocketContributions={totalOutOfPocketContributions}
              totalHealthCoinRewardsEarnedExcludingTopUps={
                totalHealthCoinRewardsEarnedExcludingTopUps
              }
              totalHealthCoinRewardsEarned={totalHealthCoinRewardsEarned}
              ongoingClinicPurchasesAmount={ongoingClinicPurchasesAmount}
              ongoingClinicPurchasesFrequencyOption={
                ongoingClinicPurchasesFrequencyOption
              }
              rewardRate={rewardRate}
              topUpAmountForInitialBalance={topUpAmountForInitialBalance}
              topUpAmountForIndividualOngoingDeposit={
                topUpAmountForIndividualOngoingDeposit
              }
              generateGraphData={generateGraphData}
            />
          </div>
        </Frame>

        {showTopUpTable && !admin && (
          <TopUpTableModal
            onClose={handleTopUpTableToggle}
            currentTopUpCampaign={currentTopUpCampaign}
            showAddFundsButton
          />
        )}
        {showTopUpTable && admin && (
          <TopUpTableModal
            onClose={handleTopUpTableToggle}
            currentTopUpCampaign={currentTopUpCampaign}
          />
        )}
      </div>
    </>
  );
}

RewardCalculator.propTypes = {
  backUrl: PropTypes.string,
  printMode: PropTypes.bool,
  hideTreatmentCategorySwitcher: PropTypes.bool,
  hideModeSwitcher: PropTypes.bool,
  hideAutoGrowSwitcher: PropTypes.bool,
  defaultTreatmentCategory: PropTypes.string,
  defaultInitialBalance: PropTypes.number,
  defaultGoalBalance: PropTypes.number,
  defaultBalanceDurationInMonths: PropTypes.number,
  defaultCalculatorMode: PropTypes.string,
  defaultAutoGrowMode: PropTypes.string,
  defaultOngoingDepositsAmount: PropTypes.number,
  defaultOngoingDepositsFrequency: PropTypes.string,
  defaultOngoingClinicPurchasesAmount: PropTypes.number,
  defaultOngoingClinicPurchasesFrequency: PropTypes.string,
  defaultRewardRate: PropTypes.number,
  defaultTopUpAmountForInitialBalance: PropTypes.number,
  defaultTopUpAmountForIndividualOngoingDeposit: PropTypes.number,
  defaultTimeToHitGoalInMonths: PropTypes.number,
};

export default RewardCalculator;
